import { focusHaloFlush, panelMotion } from "@pax/ui";
import { Check, Copy } from "lucide-react";
import { useState } from "react";

const COPIED_MS = 1_200;

/** Copies `text`, and says so for a moment. */
function useCopy(text: string) {
  const [copied, setCopied] = useState(false);
  const copy = () =>
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), COPIED_MS);
    });
  return [copied, copy] as const;
}

/** An id that copies itself when pressed. */
export function CopyableId({ value }: { value: string }) {
  const [copied, copy] = useCopy(value);
  return (
    <button
      type="button"
      title={`Copy ${value}`}
      onClick={copy}
      className={`-mx-1 block max-w-full truncate rounded-md px-1 text-left font-mono text-xs hover:bg-foreground/5 ${focusHaloFlush} ${panelMotion}`}
    >
      {copied ? <span className="font-sans text-muted-foreground">Copied</span> : value}
    </button>
  );
}

/** A quiet copy icon that widens, for a moment, to say what it copied. */
export function CopyButton({
  text,
  label,
  copied: said = "Copied",
}: {
  text: string;
  /** What pressing it copies, for its tooltip and screen readers. */
  label: string;
  copied?: string;
}) {
  const [copied, copy] = useCopy(text);
  const Icon = copied ? Check : Copy;
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={copy}
      className={`flex h-6 items-center rounded-lg px-1.5 text-xs whitespace-nowrap text-muted-foreground/70 hover:bg-foreground/5 hover:text-foreground ${copied ? "text-foreground" : ""} ${focusHaloFlush} ${panelMotion}`}
    >
      <span
        aria-hidden
        className={`grid transition-[grid-template-columns] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${copied ? "grid-cols-[1fr]" : "grid-cols-[0fr]"}`}
      >
        <span className="overflow-hidden">
          <span className="pr-1.5">{said}</span>
        </span>
      </span>
      <Icon className="size-3 shrink-0" />
    </button>
  );
}
